import { useMemo } from 'react';

const toPersianDigits = (num: number | string): string => {
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return num.toString().replace(/\d/g, (x) => persianDigits[parseInt(x)]);
};

const toSafeDate = (value: number | string | Date): Date => {
  if (value instanceof Date) return value;

  if (typeof value === 'number') {
    const isSeconds = value < 1e12;
    return new Date(isSeconds ? Math.floor(value * 1000) : value);
  }

  const parsed = new Date(value);

  if (isNaN(parsed.getTime()) && !isNaN(Number(value))) {
    const numVal = Number(value);
    const isSeconds = numVal < 1e12;
    return new Date(isSeconds ? Math.floor(numVal * 1000) : numVal);
  }

  return parsed;
};

export const useRelativeTime = (dateInput: string | number | Date) => {
  const relativeTime = useMemo(() => {
    if (!dateInput) return '';

    const now = new Date();
    const past = toSafeDate(dateInput);

    if (isNaN(past.getTime())) return '';

    const diffInMs = now.getTime() - past.getTime();

    if (diffInMs <= 0) return 'لحظاتی پیش';

    const seconds = Math.floor(diffInMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days >= 28) {
      const months = Math.floor(days / 30);
      const remainingDays = days % 30;

      if (days < 30) {
        return '۱ ماه پیش';
      }

      if (months === 1) {
        return remainingDays > 0
          ? `۱ ماه و ${toPersianDigits(remainingDays)} روز پیش`
          : '۱ ماه پیش';
      } else {
        return remainingDays > 0
          ? `${toPersianDigits(months)} ماه و ${toPersianDigits(remainingDays)} روز پیش`
          : `${toPersianDigits(months)} ماه پیش`;
      }
    }

    if (days >= 6) {
      const weeks = Math.floor(days / 7);
      const remainingDays = days % 7;

      if (days === 6 || (weeks === 1 && remainingDays === 0)) {
        return '۱ هفته پیش';
      }

      const displayWeeks = weeks === 0 ? 1 : weeks;
      return remainingDays > 0
        ? `${toPersianDigits(displayWeeks)} هفته و ${toPersianDigits(remainingDays)} روز پیش`
        : `${toPersianDigits(displayWeeks)} هفته پیش`;
    }

    if (days >= 1) {
      const remainingHours = hours % 24;

      if (days === 1) {
        return remainingHours > 0
          ? `۱ روز ${toPersianDigits(remainingHours)} ساعت پیش`
          : '۱ روز پیش';
      } else {
        return remainingHours > 0
          ? `${toPersianDigits(days)} روز ${toPersianDigits(remainingHours)} ساعت پیش`
          : `${toPersianDigits(days)} روز پیش`;
      }
    }

    if (hours >= 1) {
      const remainingMinutes = minutes % 60;

      if (hours === 1) {
        return remainingMinutes > 0
          ? `۱ ساعت ${toPersianDigits(remainingMinutes)} دقیقه پیش`
          : '۱ ساعت پیش';
      } else {
        return remainingMinutes > 0
          ? `${toPersianDigits(hours)} ساعت ${toPersianDigits(remainingMinutes)} دقیقه پیش`
          : `${toPersianDigits(hours)} ساعت پیش`;
      }
    }

    return 'دقایقی پیش';
  }, [dateInput]);

  return relativeTime;
};
